import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  LayoutDashboard, TrendingUp, Award, CreditCard, Sparkles,
  ChevronRight, ArrowUp, ArrowDown, Minus, History, Image as ImageIcon
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getScoreColor, getScoreLabel } from '../utils/scoreHelpers';

export default function Dashboard() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [progress, setProgress] = useState(null);
  const [achievements, setAchievements] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [overviewRes, progressRes, achievementsRes, historyRes] = await Promise.all([
        api.get('/dashboard/overview'),
        api.get('/dashboard/progress'),
        api.get('/dashboard/achievements'),
        api.get('/analysis/history?limit=50')
      ]);

      setOverview(overviewRes.data);
      setProgress(progressRes.data);
      setAchievements(achievementsRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-bastion-black"></div>
      </div>
    );
  }

  return (
    <div className="py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-bastion-black">
              Analysis History
            </h1>
            <p className="text-bastion-gray-700 mt-1">
              View all your handwriting analysis records
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex gap-3">
            <Link to="/analysis" className="btn-primary">
              <Sparkles className="w-4 h-4" />
              New Analysis
            </Link>
            <Link to="/tools" className="btn-secondary">
              Practice Sheets
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={CreditCard}
            label="Available Credits"
            value={overview?.credits || 0}
            color="bg-blue-500"
          />
          <StatCard
            icon={LayoutDashboard}
            label="Total Analyses"
            value={overview?.totalAnalyses || 0}
            color="bg-purple-500"
          />
          <StatCard
            icon={TrendingUp}
            label="Latest Score"
            value={overview?.latestScore || '-'}
            color="bg-green-500"
          />
          <StatCard
            icon={Award}
            label="Achievements"
            value={achievements?.earned?.length || 0}
            color="bg-orange-500"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Progress Chart */}
          <div className="lg:col-span-2 card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Progress Over Time</h2>
              {progress?.improvement !== null && (
                <div className={`flex items-center gap-1 text-sm font-medium ${
                  progress.improvement > 0 ? 'text-green-500' :
                  progress.improvement < 0 ? 'text-red-500' : 'text-bastion-gray-500'
                }`}>
                  {progress.improvement > 0 ? <ArrowUp className="w-4 h-4" /> :
                   progress.improvement < 0 ? <ArrowDown className="w-4 h-4" /> :
                   <Minus className="w-4 h-4" />}
                  {Math.abs(progress.improvement)} points
                </div>
              )}
            </div>

            {progress?.dataPoints?.length > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={progress.dataPoints}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      stroke="#737373"
                      fontSize={12}
                    />
                    <YAxis domain={[0, 100]} stroke="#737373" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#fff',
                        border: '1px solid #E5E5E5',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(date) => new Date(date).toLocaleDateString()}
                    />
                    <Line
                      type="monotone"
                      dataKey="overall"
                      stroke="#2383E2"
                      strokeWidth={2}
                      dot={{ fill: '#2383E2', strokeWidth: 0 }}
                      name="Overall Score"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex flex-col items-center justify-center text-bastion-gray-500">
                <TrendingUp className="w-12 h-12 mb-3 opacity-50" />
                <p>No analysis data yet</p>
                <Link to="/analysis" className="text-bastion-black hover:underline mt-2">
                  Start your first analysis
                </Link>
              </div>
            )}
          </div>

          {/* Achievements */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Achievements</h2>
              <span className="text-sm text-bastion-gray-500">
                {achievements?.earned?.length || 0} / {achievements?.available?.length || 0}
              </span>
            </div>

            <div className="space-y-3">
              {achievements?.available?.slice(0, 5).map((achievement) => (
                <div
                  key={achievement.type}
                  className={`flex items-center gap-3 p-3 rounded-lg ${
                    achievement.earned ? 'bg-green-50' : 'bg-bastion-gray-50'
                  }`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    achievement.earned ? 'bg-green-500' : 'bg-bastion-gray-300'
                  }`}>
                    <Award className={`w-5 h-5 ${
                      achievement.earned ? 'text-white' : 'text-bastion-gray-500'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className={`font-medium ${
                      achievement.earned ? 'text-bastion-black' : 'text-bastion-gray-500'
                    }`}>
                      {achievement.name}
                    </p>
                    <p className="text-xs text-bastion-gray-500">
                      {achievement.description}
                    </p>
                  </div>
                  {achievement.earned && (
                    <span className="text-green-500 text-xs">Earned!</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Analysis History Cards */}
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-6">
            <History className="w-5 h-5 text-bastion-black" />
            <h2 className="text-lg font-semibold">Your Analyses</h2>
          </div>

          {history?.analyses?.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {history.analyses.map((analysis) => (
                <AnalysisCard key={analysis.id} analysis={analysis} />
              ))}
            </div>
          ) : (
            <div className="card text-center py-12">
              <ImageIcon className="w-12 h-12 text-bastion-gray-300 mx-auto mb-3" />
              <p className="text-bastion-gray-700 font-medium mb-1">No analyses yet</p>
              <p className="text-bastion-gray-500 text-sm mb-4">
                Upload a handwriting sample to get your first analysis
              </p>
              <Link to="/analysis" className="btn-primary inline-flex">
                <Sparkles className="w-4 h-4" />
                Start Your First Analysis
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          <QuickAction
            title="Analyze Handwriting"
            description="Upload a sample for AI analysis"
            link="/analysis"
            icon={Sparkles}
          />
          <QuickAction
            title="Practice Worksheets"
            description="Generate custom practice sheets"
            link="/tools"
            icon={LayoutDashboard}
          />
          <QuickAction
            title="Get More Credits"
            description="Purchase analysis credits"
            link="/pricing"
            icon={CreditCard}
          />
        </div>
      </div>
    </div>
  );
}

function AnalysisCard({ analysis }) {
  const apiBase = import.meta.env.VITE_API_URL || '';
  const imageUrl = analysis.imageUrl ? `${apiBase}${analysis.imageUrl}` : null;
  const date = new Date(analysis.createdAt);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
  const formattedTime = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  });

  return (
    <Link
      to={`/analysis/${analysis.publicId}`}
      className="group block bg-white rounded-xl border border-bastion-gray-200 overflow-hidden hover:shadow-lg hover:border-bastion-gray-300 transition-all duration-200"
    >
      {/* Image thumbnail */}
      <div className="aspect-[4/3] bg-bastion-gray-100 overflow-hidden relative">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Handwriting sample"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon className="w-10 h-10 text-bastion-gray-300" />
          </div>
        )}

        {/* Score badge overlay */}
        <div className="absolute bottom-2 left-2">
          <div className={`inline-flex items-baseline gap-1 px-2.5 py-1 rounded-lg bg-white/90 backdrop-blur-sm shadow-sm`}>
            <span className={`text-xl font-bold ${getScoreColor(analysis.overallScore)}`}>
              {analysis.overallScore}
            </span>
            <span className="text-xs text-bastion-gray-700 font-medium">
              /100
            </span>
          </div>
        </div>
      </div>

      {/* Card info */}
      <div className="p-3">
        <div className="flex items-center justify-between mb-1">
          <span className={`text-sm font-semibold ${getScoreColor(analysis.overallScore)}`}>
            {analysis.level || getScoreLabel(analysis.overallScore)}
          </span>
          <ChevronRight className="w-4 h-4 text-bastion-gray-400 group-hover:text-bastion-black transition-colors" />
        </div>

        {/* Score breakdown mini bars */}
        <div className="flex gap-1 mb-2">
          {['legibility', 'consistency', 'fluency', 'structure', 'penControl'].map((dim) => (
            <div key={dim} className="flex-1">
              <div className="h-1 bg-bastion-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-bastion-blue-500 rounded-full"
                  style={{ width: `${analysis.scores[dim]}%` }}
                />
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs text-bastion-gray-700">
          {formattedDate} at {formattedTime}
        </p>
      </div>
    </Link>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-sm text-bastion-gray-700">{label}</p>
          <p className="text-2xl font-bold text-bastion-black">{value}</p>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ title, description, link, icon: Icon }) {
  return (
    <Link to={link} className="card-hover flex items-center justify-between group">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-bastion-blue-50 rounded-xl flex items-center justify-center">
          <Icon className="w-6 h-6 text-bastion-black" />
        </div>
        <div>
          <h3 className="font-semibold text-bastion-black">{title}</h3>
          <p className="text-sm text-bastion-gray-700">{description}</p>
        </div>
      </div>
      <ChevronRight className="w-5 h-5 text-bastion-gray-400 group-hover:text-bastion-black transition-colors" />
    </Link>
  );
}
